
import Vue from 'vue';
import helper from '../../../utils/helper';
import ArrowDown from '../../../Icons/ArrowDownLight.vue';
import SearchIcon from '../../../Icons/SearchIcon.vue';
import Button from '../Button/Button.vue';
import EuDropdown from '../Dropdown/Dropdown.vue';
import FilterIcon from '../../../Icons/FilterIcon.vue';

/**
- To modify the styling of filter section, select <b>.eu-inputfilter-dropdown > .eu-dropdown-container</b> 
- To modify text inside filter section (for example; text needs a longer wrapper / add ellipsis overflow if text is too long) select <b>.eu-inputfilter-dropdown .eu-dropdown-content</b> 
- Output: {select: "choosenFilter", value: "textInput"}
**/
export default Vue.extend<any, any, any, any>({
  name: 'InputWithFilter',
  data() {
    return {
      focus: false,
      open: false,
      selectedItem: {}
    };
  },
  components: {
    ArrowDown,
    SearchIcon,
    Button,
    EuDropdown
  },
  props: {
    /**
     * Value of input
     */
    value: {
      type: [String, Number, Object],
      required: true
    },
    /**
     * The ID, can be string or number
     */
    id: {
      type: [Number, String]
    },
    /**
     * Set maximum length for input value (for all types)
     */
    maxLength: {
      type: Number,
      default: Number.POSITIVE_INFINITY
    },
    /**
     * Set minimum length for input value (for all types)
     */
    minLength: {
      type: Number,
      default: 0
    },
    /**
     * Set exact length for input value (for all types)
     */
    exactLength: {
      type: Number
    },
    /**
     * Set placeholder in the input
     */
    inputPlaceholder: {
      type: String,
      default: 'Placeholder here'
    },
    /**
     * Set placeholder in the dropdown
     */
    dropdownPlaceholder: {
      type: String,
      default: 'Filter by'
    },
    /**
     * Set input on error state
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Set input on success state
     */
    success: {
      type: Boolean,
      default: false
    },
    /**
     * Change input formatter type (example: currency)
     */
    format: {
      type: String,
      default: ''
    },
    /**
     * Set the separator for currency (example: , or .)
     */
    separator: {
      type: String,
      default: ','
    },
    /**
     * Set filter icon color
     */
    filterColor: {
      type: String,
      default: '#ff6a00'
    },
    /**
     * Set search color
     */
    searchColor: {
      type: String,
      default: '#0a2062'
    },
    /**
     * To apply dropdown selection on left side of input
     */
    selectionOptions: {
      type: Array,
      default: () => [
        {
          label: '',
          value: ''
        }
      ]
    },
    /**
     * Clickonly dropdown
     */
    clickonly: {
      type: Boolean,
      default: false
    },
    /**
     * Set filter icon inside dropdown
     */
    filterIcon: {
      type: Object,
      default: () => FilterIcon
    },
    /**
     * Set filter icon size
     */
    filterIconSize: {
      type: String,
      default: '16px'
    }
  },
  created() {
    if (this.value && this.selectionOptions) {
      const selectionFind = (this.selectionOptions as any[]).find(selection => selection.value === this.value.select);
      this.selectedItem = selectionFind;
    }
  },
  computed: {
    containerClasses() {
      return {
        focus: this.focus,
        error: this.error,
        success: this.success,
        disabled: this.$attrs.disabled === true,
        openborder: this.open
      };
    },
    isIntegerNumber() {
      return this.$attrs.type === 'number' || this.format === 'currency';
    },
    modifiedVal() {
      let value = '';
      if (this.value.value) {
        value = this.value.value;
        if (this.format === 'currency') {
          const stringValue = String(value);
          return this.currencyFormat(stringValue.replace(/[.,\s]/g, ''));
        }
      }
      return value;
    }
  },
  methods: {
    currencyFormat(val: String) {
      const parts = val.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.separator);
      return parts.join(this.separator);
    },
    handleFocus(e: Event) {
      this.focus = true;
      /**
       * On focus
       * @event focus
       * @type {function}
       */
      this.$emit('focus', e);
    },
    handleBlur(e: Event) {
      this.focus = false;
      let valueData = (e.target as HTMLInputElement).value;
      if (this.format === 'currency' || this.format === 'decimals') {
        valueData = valueData.replace(/[.,\s]/g, '');
      }
      if (this.minLength) {
        /**
         * Will check whether the input length doesn't fit minLength/exactLength props given. Example, if minLength is 5 and value length is 3; inputState = false
         * @event inputState
         * @type {function}
         */
        this.$emit('inputState', valueData.length >= this.minLength);
      }
      if (this.exactLength) {
        this.$emit('inputState', valueData.length === this.exactLength);
      }
      /**
       * On blur
       * @event blur
       * @type {function}
       */
      this.$emit('blur', e);
    },
    handleInput(e: Event) {
      let valueData = (e.target as HTMLInputElement).value;
      let valueObject = {};
      if (this.format === 'currency') {
        const stringValue = String(valueData);
        valueData = this.currencyFormat(stringValue.replace(/[.,\s]/g, ''));
      }
      valueObject = {
        select: this.value ? this.value.select : this.selectedItem.value,
        value: valueData
      };
      /**
       * Method to input value
       * @event input
       * @type {function}
       */
      this.$emit('input', valueObject);
    },
    handleChange(e: Event) {
      let valueData = (e.target as HTMLInputElement).value;
      if (this.format === 'currency' || this.format === 'decimals') {
        valueData = valueData.replace(/[.,\s]/g, '');
      }
      if (valueData.length > this.maxLength || (this.format === 'currency' && !helper.isNumeric(valueData))) {
        this.$emit('input', '');
      }
      /**
       * On Change
       * @event change
       * @type {function}
       */
      this.$emit('change', e);
    },
    handleKeyPress(e: any) {
      let valueData = (e.target as HTMLInputElement).value;
      if (this.format === 'currency' || this.format === 'decimals') {
        valueData = valueData.replace(/[.,\s]/g, '');
      }
      if (
        valueData.length >= (this as any).maxLength ||
        (valueData.length >= this.exactLength && this.exactLength) ||
        (/[^0-9]/g.test(e.key) && (this as any).isIntegerNumber) ||
        (!e.key.match(/^[0-9]*\.?[0-9]*$/) && (this as any).format === 'decimals')
      ) {
        e.preventDefault();
      }
      /**
       * On keypress
       * @event keypress
       * @type {function}
       */
      this.$emit('keypress');
    },
    deactivate() {
      this.open = false;
      (this.$el as HTMLElement).blur();
    },
    handleClick(e: Event) {
      /**
       * Method to click the filter dropdown
       * @event click
       * @type {function}
       */
      this.$emit('click', e);
    },
    handleSelect(val: any) {
      this.selectedItem = val;
      let valueObject = {
        select: this.selectedItem.value,
        value: this.value.value
      };
      /**
       * Method to input value
       * @event input
       * @type {function}
       */
      this.$emit('input', valueObject);
    }
  }
});
